import React, { useState, useEffect } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
} from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { GET_DOCUMENT_TYPES_LIST } from '../../graphql/queries/documentTypes.queries';
import { GET_SIGNED_UPLOAD_URL } from '../../graphql/queries/documents.queries';
import { CREATE_DOCUMENT } from '../../graphql/mutations/documents.mutations';
import {
  DocumentTypesListResponse,
  FindDocumentTypeListInput,
} from '../../types/documentTypes.types';
import {
  SignedFileUploadEndpointInput,
  SignedUploadResponse,
  CreateDocumentInput,
  CreateDocumentResponse,
} from '../../types/documents.types';

interface UploadDocumentModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (document: any) => void;
}

const UploadDocumentModal: React.FC<UploadDocumentModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [documentTypeId, setDocumentTypeId] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentTypes, setDocumentTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const [fetchDocumentTypes, { loading: loadingTypes }] = useLazyQuery<
    { chatbotDocumentTypes: DocumentTypesListResponse },
    { params: FindDocumentTypeListInput }
  >(GET_DOCUMENT_TYPES_LIST, {
    fetchPolicy: 'network-only',
  });

  const [getSignedUrl] = useLazyQuery<
    { getSignedFileUploadEndpoint: SignedUploadResponse },
    { params: SignedFileUploadEndpointInput }
  >(GET_SIGNED_UPLOAD_URL, {
    fetchPolicy: 'network-only',
  });

  const [createDocument] = useMutation<
    { createChatbotDocument: CreateDocumentResponse },
    { params: CreateDocumentInput }
  >(CREATE_DOCUMENT);

  useEffect(() => {
    if (open) {
      loadDocumentTypes();
      // Reset form
      setDescription('');
      setTags('');
      setDocumentTypeId('');
      setSelectedFile(null);
      setError(null);
      setUploadProgress(0);
    }
  }, [open]);

  const loadDocumentTypes = async () => {
    try {
      const { data } = await fetchDocumentTypes({
        variables: {
          params: {
            search: null,
            page: 1,
            pageSize: 100,
          },
        },
      });

      if (data?.chatbotDocumentTypes?.success && data.chatbotDocumentTypes.data) {
        setDocumentTypes(data.chatbotDocumentTypes.data.documentTypes);
      }
    } catch (err: any) {
      console.error('Error loading document types:', err);
      setError('Error al cargar tipos de documentos');
    }
  };

  const getMimeType = (fileName: string): string => {
    const ext = fileName.toLowerCase().split('.').pop();
    if (ext === 'doc') {
      return 'application/msword';
    } else if (ext === 'docx') {
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    }
    return '';
  };

  const isValidFileType = (file: File): boolean => {
    const fileName = file.name.toLowerCase();
    return fileName.endsWith('.doc') || fileName.endsWith('.docx');
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (isValidFileType(file)) {
        setSelectedFile(file);
        setError(null);
      } else {
        setError('Solo se permiten archivos .doc o .docx');
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (isValidFileType(file)) {
        setSelectedFile(file);
        setError(null);
      } else {
        setError('Solo se permiten archivos .doc o .docx');
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !description || !documentTypeId) {
      setError('Por favor complete todos los campos y seleccione un archivo');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setUploadProgress(10);

      const fileName = selectedFile.name;
      const mimeType = getMimeType(fileName);
      const path = `documents/iplug_documents`;

      // Step 1: Get signed URL
      setUploadProgress(20);
      const { data: signedUrlData } = await getSignedUrl({
        variables: {
          params: {
            fileName,
            mimeType,
            path,
          },
        },
      });

      if (!signedUrlData?.getSignedFileUploadEndpoint?.success) {
        const errorMessage =
          signedUrlData?.getSignedFileUploadEndpoint?.message ||
          'Error al obtener URL de subida';
        setError(errorMessage);
        setUploading(false);
        return;
      }

      const uploadData = signedUrlData.getSignedFileUploadEndpoint.data;
      if (!uploadData) {
        setError('No se recibió información de subida');
        setUploading(false);
        return;
      }

      // Step 2: Upload file to signed URL
      setUploadProgress(40);
      try {
        await axios.put(uploadData.uploadUrl, selectedFile, {
          headers: {
            'Content-Type': mimeType,
            'x-amz-acl': 'public-read'
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const progress = Math.round((progressEvent.loaded * 40) / progressEvent.total) + 40;
              setUploadProgress(progress);
            }
          },
        });
      } catch (uploadError: any) {
        console.error('Error uploading file:', uploadError);
        setError('Error al subir el archivo al servidor');
        setUploading(false);
        return;
      }

      // Step 3: Create document record and vectorize
      setUploadProgress(80);
      const tagsArray = tags
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0);

      const { data: createDocData } = await createDocument({
        variables: {
          params: {
            fileName: uploadData.fileName,
            path: uploadData.path,
            uri: uploadData.uri,
            mimeType: uploadData.mimeType,
            fileSize: selectedFile.size,
            documentTypeId,
            description,
            tags: tagsArray,
          },
        },
      });

      if (!createDocData?.createChatbotDocument?.success) {
        const errorMessage =
          createDocData?.createChatbotDocument?.message ||
          'Error al procesar el documento';
        const errors = createDocData?.createChatbotDocument?.errors || [];
        const fullErrorMessage =
          errors.length > 0 ? `${errorMessage}: ${errors.join(', ')}` : errorMessage;
        setError(fullErrorMessage);
        setUploading(false);
        return;
      }

      setUploadProgress(100);

      // Success - return the created document
      if (createDocData.createChatbotDocument.data) {
        onSuccess(createDocData.createChatbotDocument.data);
        onClose();
      }
    } catch (err: any) {
      console.error('Error in upload process:', err);
      setError(err.message || 'Error en el proceso de subida');
    } finally {
      setUploading(false);
    }
  };

  const isFormValid =
    selectedFile && description.trim() && documentTypeId && !uploading;

  return (
    <Dialog open={open} onClose={uploading ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Vectorizar Documento</DialogTitle>
      <DialogContent>
        {loadingTypes ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {uploading && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Procesando documento...
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  {uploadProgress}%
                </Typography>
              </Box>
            )}

            <TextField
              fullWidth
              label="Descripción"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              margin="normal"
              multiline
              rows={3}
              required
              disabled={uploading}
            />

            <FormControl fullWidth margin="normal" required disabled={uploading}>
              <InputLabel>Tipo de Documento</InputLabel>
              <Select
                value={documentTypeId}
                onChange={(e) => setDocumentTypeId(e.target.value)}
                label="Tipo de Documento"
              >
                {documentTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Tags (separados por coma)"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              margin="normal"
              disabled={uploading}
              helperText="Ejemplo: manual, tutorial, guía"
            />

            <Box
              sx={{
                mt: 3,
                p: 4,
                border: `2px dashed ${dragActive ? '#1976d2' : '#ccc'}`,
                borderRadius: 2,
                backgroundColor: dragActive ? 'rgba(25, 118, 210, 0.04)' : '#fafafa',
                textAlign: 'center',
                cursor: uploading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': uploading
                  ? {}
                  : {
                      borderColor: '#1976d2',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    },
              }}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => !uploading && document.getElementById('file-input')?.click()}
            >
              <input
                id="file-input"
                type="file"
                accept=".doc,.docx"
                onChange={handleFileChange}
                style={{ display: 'none' }}
                disabled={uploading}
              />
              <CloudUploadIcon sx={{ fontSize: 48, color: '#1976d2', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {selectedFile ? selectedFile.name : 'Arrastra un archivo o haz clic para seleccionar'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Solo archivos .doc o .docx
              </Typography>
              {selectedFile && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Tamaño: {(selectedFile.size / 1024).toFixed(2)} KB
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" disabled={uploading}>
          Cancelar
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={!isFormValid}
          startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
        >
          {uploading ? 'Subiendo...' : 'Subir y Vectorizar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadDocumentModal;
